import * as API from '@/API';
import { OKResponse } from '@/API';
import UserDefaultsModelDTO from '@/RazorTS/UserDefaults/DTO/UserDefaultsModelDTO';
import FilingCabinetStore from '@/Stores/FilingCabinetStore';
import { t } from 'i18next';
import { makeAutoObservable } from 'mobx';

interface DisplayOptions {
    loginInfoOpen: boolean;
    generalInfoOpen: boolean;
    technicianInfoOpen: boolean;
    radiologistInfoOpen: boolean;
    reportDefaultsOpen: boolean;
    speechRecognitionOpen: boolean;
}

export interface OptionLists {
    statuses: [];
    displayLengths: [];
    displayLanguages: [];
    availableDatesForShowReportsFrom: [];
    speechRecognitions: [];
    eolG2: [];
}

export type ErrorResponse = {
    [alias in keyof UserDefaultsModelDTO]: string
};

interface StateData {
    userModel: UserDefaultsModelDTO;
    view: DisplayOptions;
    optionLists: OptionLists | undefined;
    errors: ErrorResponse | undefined;
}

export default class UserDefaultsStore {
    state: StateData = {
        userModel: {} as UserDefaultsModelDTO,
        view: {
            loginInfoOpen: true,
            generalInfoOpen: true,
            technicianInfoOpen: true,
            radiologistInfoOpen: true,
            reportDefaultsOpen: true,
            speechRecognitionOpen: true,
        },
        optionLists: undefined,
        errors: undefined,
    };

    private readonly fcStore: FilingCabinetStore<StateData>;

    constructor(fcStore: FilingCabinetStore<StateData>, userId: string | undefined) {
        this.fcStore = fcStore;

        makeAutoObservable(this);
        this.fcStore.initializeState(this.initOld, () => void this.initNew(userId));
        this.fcStore.updateTitle(t('UserDefaults'));
    }

    private readonly initOld = (state: StateData): void => {
        this.setState(state);
    };

    private readonly initNew = async (userId: string | undefined): Promise<void> => {
        await Promise.all([API.user.getUserDefaultsDTO(userId), API.user.getUserDefaultsHydrationData()]).then(([userDTO, hydrationData]) => {
            this.setState({
                ...this.state,
                userModel: userDTO,
                optionLists: hydrationData,
            });
        });
    };

    setState = (newState: StateData): void => {
        this.state = newState;
        this.fcStore.updateState(this.state);
    };

    setReportTemplate(val: string) {
        this.state.userModel.DefaultReportTemplate = val;
    }

    toggleCollapsible(e: React.MouseEvent<HTMLElement>, tab: keyof DisplayOptions) {
        e.preventDefault();

        this.state.view[tab] = !this.state.view[tab];
        this.fcStore.updateState(this.state);
    }

    setSelectedStatusToChoose(checked: boolean | 'indeterminate', s: number) {
        if (checked == 'indeterminate') {
            return;
        }

        if (!checked) {
            this.state.userModel.SelectedStatusToChooseIDs = this.state.userModel.SelectedStatusToChooseIDs.filter((x) => x != s);
        }
        else {
            this.state.userModel.SelectedStatusToChooseIDs.push(s);
        }

        this.fcStore.updateState(this.state);
    }

    setSelectedStatusToChange(checked: boolean | 'indeterminate', s: number) {
        if (checked == 'indeterminate') {
            return;
        }

        if (!checked) {
            this.state.userModel.SelectedStatusToChangeIDs = this.state.userModel.SelectedStatusToChangeIDs.filter((x) => x != s);
        }
        else {
            this.state.userModel.SelectedStatusToChangeIDs.push(s);
        }

        this.fcStore.updateState(this.state);
    }

    setSelectedAvailableStatusIds(checked: boolean | 'indeterminate', s: number) {
        if (checked == 'indeterminate') {
            return;
        }

        if (!checked) {
            this.state.userModel.SelectedStatusIDs = this.state.userModel.SelectedStatusIDs.filter((x) => x != s);
        }
        else {
            this.state.userModel.SelectedStatusIDs.push(s);
        }

        this.fcStore.updateState(this.state);
    }

    setProperty<T, K extends keyof T>(obj: T, key: K, value: T[K]): void {
        obj[key] = value;
        this.fcStore.updateState(this.state);
    }

    setSelectedDragonLanguages(options: { value: string; label: string }[]) {
        this.state.userModel.DragonLanguages = options.map((x) => x.value);
        this.fcStore.updateState(this.state);
    }

    setInternalRadiologist(option: { label: string; value: number }) {
        if (option == null) {
            this.removeInternalRadiologist();
            return;
        }

        this.state.userModel.Radiologist_Text = option.label;
        this.state.userModel.RadiologistID = option.value;
        this.fcStore.updateState(this.state);
    }

    removeInternalRadiologist() {
        this.state.userModel.Radiologist_Text = '';
        this.state.userModel.RadiologistID = undefined;
        this.fcStore.updateState(this.state);
    }

    setErrors(errors: ErrorResponse) {
        this.state.errors = errors;
        this.fcStore.updateState(this.state);
    }

    setRecognitionOption(checked: boolean) {
        this.state.userModel.Recognition = checked;
        this.state.userModel.Correction = !checked;
        this.state.userModel.SpeechMode = 1;
        this.fcStore.updateState(this.state);
    }

    setCorrectionOption(checked: boolean) {
        this.state.userModel.Correction = checked;
        this.state.userModel.Recognition = !checked;
        this.state.userModel.SpeechMode = 2;
        this.fcStore.updateState(this.state);
    }

    setShowDaysInFuture(val: string) {
        const newValue = Number.parseInt(val);
        if (Number.isNaN(newValue)) {
            return;
        }
        this.state.userModel.ShowDaysInFuture = newValue;
        this.fcStore.updateState(this.state);
    }

    async save() {
        await API.user.saveUserDefaultsNew<OKResponse & { shouldReload: boolean }, ErrorResponse>(
            this.state.userModel,
            (res: OKResponse & { shouldReload: boolean }) => {
                void this.fcStore._fc.removeActive();
                if (res.shouldReload) {
                    window.location.reload();
                }
            },
            (res: ErrorResponse) => {
                this.setErrors(res);
                console.error('Server errors on SaveUserDefaults...');
            },
        );
    }
}
